import type { Transition, Variants } from 'framer-motion'

/**
 * Sistema de motion de Match&Play.
 *
 * Un único sitio para las duraciones, curvas y variants de animación, para que
 * todo tenga la MISMA personalidad: brutalista = **corto, físico y con un pelín
 * de rebote**, nunca fundidos lentos y flotantes (eso lee a genérico).
 *
 * Reglas de uso:
 * - Anima solo `transform`/`opacity` (GPU; rendimiento en el webview nativo).
 * - El respeto a `prefers-reduced-motion` es GLOBAL: `App` monta
 *   `<MotionConfig reducedMotion="user">`, que hace que framer omita las
 *   animaciones de `transform` cuando el usuario pide menos movimiento (la
 *   opacidad se mantiene). Por eso las variants de aquí no necesitan lógica de
 *   reduced-motion propia.
 * - Usa componentes `m.*` (no `motion.*`) bajo el `<LazyMotion>` de `App` para
 *   mantener el bundle pequeño.
 */

/** Duraciones en segundos (unidad de framer). */
export const DURATION = {
  fast: 0.15,
  base: 0.22,
  slow: 0.32,
} as const

/** Spring brutalista estándar: rígido, con un pelín de overshoot físico. */
export const SPRING: Transition = { type: 'spring', stiffness: 480, damping: 30, mass: 0.8 }

/** Spring más "pop" para entradas de modal/badge (overshoot un poco más visible). */
export const SPRING_POP: Transition = { type: 'spring', stiffness: 560, damping: 26, mass: 0.7 }

/** Curva de salida suave (easeOutExpo-ish) para fades sin spring. */
export const EASE_OUT = [0.16, 1, 0.3, 1] as [number, number, number, number]

/** Backdrop de modal: fundido puro. */
export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: DURATION.base, ease: 'easeOut' } },
  exit: { opacity: 0, transition: { duration: DURATION.fast, ease: 'easeIn' } },
}

/** Panel de modal: "pop" brutal (entra con escala + leve subida, spring). */
export const modalPanelVariants: Variants = {
  hidden: { opacity: 0, scale: 0.96, y: 8 },
  visible: { opacity: 1, scale: 1, y: 0, transition: SPRING_POP },
  exit: { opacity: 0, scale: 0.97, y: 6, transition: { duration: DURATION.fast, ease: 'easeIn' } },
}

/** Transición de página: fade + leve deslizamiento vertical. */
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: DURATION.base, ease: EASE_OUT } },
}

/** Dropdown/popover anclado: entra con pop desde el ancla (usar origin-* en CSS). */
export const dropdownVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95, y: -6 },
  visible: { opacity: 1, scale: 1, y: 0, transition: SPRING_POP },
}

/** Badge/contador: aparece con un pop de escala (spring). */
export const badgePopVariants: Variants = {
  hidden: { scale: 0 },
  visible: { scale: 1, transition: SPRING_POP },
}
